// Login status returned by GET app/users/{userId}/loginStatus and on Login Users rows (LoginStatusDto).
export type LoginCheck = {
  code: string
  // CASE_MANAGER / SPECIALIST / CONTRACTOR; null for the login account itself
  profile?: string | null
  passed: boolean
  value?: string | null
}

export type LoginStatus = {
  // Account Status of the login account (same as Login Users)
  isActive: boolean
  // The linked Case Manager / Specialist / Contractor allows login; null when there is no such profile
  loginStatusEnabled?: boolean | null
  // The answer staff need: every check passes
  canLogIn?: boolean
  checks?: LoginCheck[]
}

export const LOGIN_CHECK = {
  ACCOUNT_ACTIVE: 'ACCOUNT_ACTIVE',
  PROFILE_ACTIVE: 'PROFILE_ACTIVE',
  ENABLE_LOGIN: 'ENABLE_LOGIN',
  RECRUITMENT_STATUS: 'RECRUITMENT_STATUS',
  // Only built here, for a server that does not return checks yet: the profile part as one row
  PROFILE_LOGIN: 'PROFILE_LOGIN',
}

export const LOGIN_PROFILE_NAMES = {
  CASE_MANAGER: 'Case Manager',
  SPECIALIST: 'Specialist',
  CONTRACTOR: 'Contractor',
}

const profileName = (check: LoginCheck) =>
  LOGIN_PROFILE_NAMES[check.profile] ?? 'Profile'

// Checklist order: what staff change on the profile first, the login account last.
const CHECK_ORDER = [
  LOGIN_CHECK.ENABLE_LOGIN,
  LOGIN_CHECK.RECRUITMENT_STATUS,
  LOGIN_CHECK.PROFILE_ACTIVE,
  LOGIN_CHECK.PROFILE_LOGIN,
  LOGIN_CHECK.ACCOUNT_ACTIVE,
]

export function sortLoginChecks(checks: LoginCheck[] = []) {
  return [...checks].sort(
    (a, b) => CHECK_ORDER.indexOf(a.code) - CHECK_ORDER.indexOf(b.code)
  )
}

// A check reads "<name>: <current value>"; when it fails the row adds "Required: <required>".
// help is the tooltip (what it is and how to fix it); reason is the one-line failure text used in
// warnings and on Login Users.
export function describeLoginCheck(check: LoginCheck) {
  const profile = profileName(check)
  const describe = (name: string, required: string, help: string) => ({
    name,
    required,
    help,
    reason: `${name} is ${check.value || 'not set'} (required: ${required})`,
  })

  switch (check.code) {
    case LOGIN_CHECK.ACCOUNT_ACTIVE:
      return describe(
        'Account Status',
        'Active',
        'Status of the login account on Login Users. It becomes Inactive when staff disable it, or automatically after a long time without logging in. Use Enable next to Account Status (beside Username) to reactivate it; the user receives a re-enablement email.'
      )
    case LOGIN_CHECK.PROFILE_ACTIVE:
      return describe(
        `${profile} Status`,
        'Active',
        `Whether this ${profile} record is Active. An inactive ${profile} cannot log in: set it to Active and save.`
      )
    case LOGIN_CHECK.ENABLE_LOGIN:
      return describe(
        'Enable Login Account',
        'Ticked',
        'The Enable Login Account checkbox on this page. Tick it and save; the first time, this creates the login account and sends the activation email.'
      )
    case LOGIN_CHECK.RECRUITMENT_STATUS:
      return describe(
        'Recruitment Status',
        'Onboarding or Onboarded',
        'Only Onboarding and Onboarded accounts can log in. Change Recruitment Status and save.'
      )
    case LOGIN_CHECK.PROFILE_LOGIN:
      return {
        ...describe(
          `${profile} login settings`,
          'Allowed',
          `Allowed when this ${profile} is Active, Enable Login Account is ticked and Recruitment Status is Onboarding or Onboarded.`
        ),
        reason: `The ${profile} does not meet the login conditions (Active, Enable Login Account ticked, Recruitment Status Onboarding or Onboarded)`,
      }
    default:
      return describe(check.code, '', '')
  }
}

// The checklist in display order. Uses the server's checks; a server that does not return them yet
// still gives isActive / loginStatusEnabled, so build the same rows from those.
export function getLoginChecks(status?: LoginStatus | null, profile?: string) {
  if (!status) {
    return []
  }
  if (status.checks?.length) {
    return sortLoginChecks(
      status.checks.filter(i => !profile || !i.profile || i.profile === profile)
    )
  }

  const checks: LoginCheck[] = []
  if (status.loginStatusEnabled != null) {
    checks.push({
      code: LOGIN_CHECK.PROFILE_LOGIN,
      profile,
      passed: status.loginStatusEnabled,
      value: status.loginStatusEnabled ? 'Allowed' : 'Not allowed',
    })
  }
  checks.push({
    code: LOGIN_CHECK.ACCOUNT_ACTIVE,
    passed: status.isActive,
    value: status.isActive ? 'Active' : 'Inactive',
  })
  return checks
}

// The user can log in only when every check passes.
export function canUserLogIn(status?: LoginStatus | null) {
  if (!status) {
    return false
  }
  return status.canLogIn ?? getLoginChecks(status).every(i => i.passed)
}

// Why the user cannot log in, in checklist order; empty when they can (or the status is unknown).
export function getLoginFailureReasons(
  status?: LoginStatus | null,
  profile?: string
) {
  return getLoginChecks(status, profile)
    .filter(i => !i.passed)
    .map(i => describeLoginCheck(i).reason)
}

const RESET_PASSWORD_MESSAGE =
  'Do you want to reset password? New password will be sent to the user automatically.'

// Confirm message before sending login details: warns when the user still could not log in.
export function getResetPasswordConfirmMessage(status?: LoginStatus | null) {
  const reasons = getLoginFailureReasons(status)
  if (reasons.length === 0) {
    return RESET_PASSWORD_MESSAGE
  }

  return `Warning: this user cannot log in with the new password until this is fixed: ${reasons.join(
    '; '
  )}. Do you still want to reset password? New password will be sent to the user automatically.`
}

// Confirm message when saving Enable Login Account on an inactive Case Manager / Contractor: the
// login account is enabled (a new account also gets its activation email), but the user still
// cannot log in until the profile itself is Active.
export function getEnableLoginOnInactiveMessage(profile: string) {
  return `Warning: this ${profile} is Inactive, so the user cannot log in until the ${profile} is set to Active. The login account will still be enabled and, if it is new, the activation email will still be sent. Do you still want to save?`
}
